import React from "react";

import {
  isColumnFrozen,
  normalizeFrozenColumnKeys,
  resolveFrozenColumnLabels,
} from "../utils/dataManagerFrozenPanes.js";
import {
  persistDataManagerFrozenPanesState,
  readDataManagerFrozenPanesState,
  resolveInitialDataManagerFrozenPanesState,
  shouldMigrateDataManagerFrozenPanesState,
} from "../utils/dataManagerFrozenPanesStorage.js";

export default function useDataManagerFrozenPanes(
  tableKey,
  freezableColumnDescriptors = [],
  { onFrozenColumnKeysChange = null, savedFrozenColumnKeys = null } = {},
) {
  const hasSavedFrozenColumnKeys = Array.isArray(savedFrozenColumnKeys);
  const normalizedSavedFrozenColumnKeys = hasSavedFrozenColumnKeys
    ? savedFrozenColumnKeys
    : [];
  const legacyFrozenColumnKeysRef = React.useRef(readDataManagerFrozenPanesState(tableKey));
  const initialFrozenColumnKeys = resolveInitialDataManagerFrozenPanesState(
    tableKey,
    savedFrozenColumnKeys,
  );
  const [frozenColumnKeys, setFrozenColumnKeys] = React.useState(
    () => initialFrozenColumnKeys,
  );
  const lastRequestedPersistenceSignatureRef = React.useRef(
    normalizedSavedFrozenColumnKeys.join("\u0000"),
  );

  const normalizedFrozenColumnKeys = React.useMemo(
    () => normalizeFrozenColumnKeys(frozenColumnKeys, freezableColumnDescriptors),
    [freezableColumnDescriptors, frozenColumnKeys],
  );

  React.useEffect(() => {
    if (!Array.isArray(savedFrozenColumnKeys)) {
      return;
    }
    const nextSavedKeys = normalizeFrozenColumnKeys(
      savedFrozenColumnKeys,
      freezableColumnDescriptors,
    );
    const nextSignature = nextSavedKeys.join("\u0000");
    lastRequestedPersistenceSignatureRef.current = nextSignature;
    setFrozenColumnKeys((currentKeys) => (
      currentKeys.join("\u0000") === nextSignature ? currentKeys : nextSavedKeys
    ));
  }, [freezableColumnDescriptors, savedFrozenColumnKeys]);

  const requestFrozenColumnKeysPersistence = React.useCallback((nextFrozenColumnKeys) => {
    if (typeof onFrozenColumnKeysChange !== "function") {
      return;
    }
    const nextSignature = nextFrozenColumnKeys.join("\u0000");
    if (nextSignature === lastRequestedPersistenceSignatureRef.current) {
      return;
    }
    lastRequestedPersistenceSignatureRef.current = nextSignature;
    onFrozenColumnKeysChange(nextFrozenColumnKeys);
  }, [onFrozenColumnKeysChange]);

  React.useEffect(() => {
    if (normalizedFrozenColumnKeys.join("\u0000") !== frozenColumnKeys.join("\u0000")) {
      setFrozenColumnKeys(normalizedFrozenColumnKeys);
      requestFrozenColumnKeysPersistence(normalizedFrozenColumnKeys);
    }
  }, [frozenColumnKeys, normalizedFrozenColumnKeys, requestFrozenColumnKeysPersistence]);

  React.useEffect(() => {
    persistDataManagerFrozenPanesState(tableKey, normalizedFrozenColumnKeys);
  }, [normalizedFrozenColumnKeys, tableKey]);

  React.useEffect(() => {
    if (shouldMigrateDataManagerFrozenPanesState(
      savedFrozenColumnKeys,
      legacyFrozenColumnKeysRef.current,
    )) {
      requestFrozenColumnKeysPersistence(normalizedFrozenColumnKeys);
    }
  }, [
    normalizedFrozenColumnKeys,
    requestFrozenColumnKeysPersistence,
    savedFrozenColumnKeys,
  ]);

  const toggleColumnFreeze = React.useCallback((columnKey) => {
    const normalizedColumnKey = typeof columnKey === "string" ? columnKey.trim() : "";
    if (!normalizedColumnKey) {
      return;
    }

    setFrozenColumnKeys((currentFrozenColumnKeys) => {
      const normalizedCurrentKeys = normalizeFrozenColumnKeys(
        currentFrozenColumnKeys,
        freezableColumnDescriptors,
      );

      if (normalizedCurrentKeys.includes(normalizedColumnKey)) {
        const nextFrozenColumnKeys = normalizedCurrentKeys.filter((key) => key !== normalizedColumnKey);
        requestFrozenColumnKeysPersistence(nextFrozenColumnKeys);
        return nextFrozenColumnKeys;
      }

      const nextFrozenColumnKeys = [...normalizedCurrentKeys, normalizedColumnKey];
      requestFrozenColumnKeysPersistence(nextFrozenColumnKeys);
      return nextFrozenColumnKeys;
    });
  }, [freezableColumnDescriptors, requestFrozenColumnKeysPersistence]);

  const unfreezePanes = React.useCallback(() => {
    setFrozenColumnKeys([]);
    requestFrozenColumnKeysPersistence([]);
  }, [requestFrozenColumnKeysPersistence]);

  const frozenColumnLabels = React.useMemo(
    () => resolveFrozenColumnLabels(normalizedFrozenColumnKeys, freezableColumnDescriptors),
    [freezableColumnDescriptors, normalizedFrozenColumnKeys],
  );

  const isFrozenColumn = React.useCallback(
    (columnKey) => isColumnFrozen(columnKey, normalizedFrozenColumnKeys),
    [normalizedFrozenColumnKeys],
  );

  return {
    frozenColumnKeys: normalizedFrozenColumnKeys,
    frozenColumnLabels,
    hasFrozenPanes: normalizedFrozenColumnKeys.length > 0,
    isFrozenColumn,
    toggleColumnFreeze,
    unfreezePanes,
  };
}
